import styled, { css } from 'styled-components';

import { ScButton, ScButtonWrapper } from '@/components/buttons/Button/styled';
import type { LoginConfig } from '@/modules/login/helpers/types';
import { device, hexToRGB } from '@/styles/helpers';

interface ScLoginThemeProps {
  styleScheme?: LoginConfig['styleScheme'];
  titleSize?: LoginConfig['titleSize'];
}

export const ScLogin = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;

  @media (${device.mobile}) {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 50px 8px 0;

    & > button {
      position: fixed;
    }
  }
`;

export const ScLoginContent = styled.div<ScLoginThemeProps>`
  position: relative;
  padding: 32px 32px 0;
  border-radius: 24px;
  color: ${({ theme, styleScheme }) => styleScheme?.color || theme.blackColor};
  background: ${({ theme, styleScheme }) =>
    styleScheme?.bg || theme.whiteColor};
  width: 100%;

  @media (${device.mobile}) {
    padding: 32px 16px 0 16px;
    margin: 0 8px 16px;
  }

  h2 {
    font-weight: bold;
    text-align: center;
    text-transform: uppercase;
    color: ${({ theme, styleScheme }) =>
      styleScheme?.headerText?.color || theme.blackColor};
    margin-bottom: ${({ styleScheme }) =>
      styleScheme?.headerText?.marginBottom || '32px'};
    font-size: ${({ titleSize }) => (titleSize === 'small' ? '28px' : '32px')};
    line-height: ${({ titleSize }) =>
      titleSize === 'small' ? '36px' : '40px'};
    letter-spacing: 0.96px;

    @media (${device.mobile}) {
      font-size: ${({ titleSize }) =>
        titleSize === 'small' ? '20px' : '32px'};
      line-height: ${({ titleSize }) =>
        titleSize === 'small' ? '26px' : '40px'};
    }
  }

  & .masked-email {
    display: inherit;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    margin-bottom: ${({ styleScheme }) =>
      styleScheme?.maskedEmail?.marginBottom || '32px'};
    color: ${({ theme, styleScheme }) =>
      styleScheme?.maskedEmail?.color || theme.mainColor};
    text-overflow: ellipsis;
    max-width: 100%;
    overflow: hidden;
  }

  & > h3 {
    font-weight: 700;
    font-size: 32px;
    line-height: 40px;
    text-align: center;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    margin-bottom: 32px;
    color: ${({ theme, styleScheme }) =>
      styleScheme?.headerText?.color || theme.blackColor};

    @media (${device.mobileXS}) {
      margin-bottom: 16px;
      font-size: 28px;
    }
  }

  .avatar {
    width: 96px;
    height: 96px;
    margin: 32px auto 16px;
  }

  .login {
    display: flex;
    justify-content: center;

    & > button {
      margin-top: 16px;
      display: flex;
      align-items: center;
      gap: 4px;

      span {
        font-weight: 400;
        font-size: 12px;
        line-height: 20px;
      }

      svg {
        transform: rotate(90deg);

        path {
          stroke: ${({ theme }) => theme.secondaryColor};
        }
      }
    }
  }
`;

interface IScLoginWrapButtonsProps {
  styleScheme?: LoginConfig['styleScheme'];
}

export const ScLoginWrapButtons = styled.div<IScLoginWrapButtonsProps>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-top: ${({ styleScheme }) => styleScheme?.ctaBtnMarginTop || '32px'};

  .recovery-btn {
    font-size: 16px;
    line-height: 24px;
    color: #8e8e93;
    margin-top: 16px;

    @media (hover: hover) {
      ${({ styleScheme }) =>
        styleScheme?.forgotPassButton?.hoverBg &&
        css`
          .aware {
            background-color: ${styleScheme.forgotPassButton.hoverBg};
          }
        `};
      &:hover {
        color: ${({ theme, styleScheme }) =>
          styleScheme?.forgotPassButton?.hoverColor || theme.blackColor};
      }
    }
  }
`;

export const ScLoginInfo = styled.div<ScLoginThemeProps>`
  display: flex;
  justify-content: center;
  gap: 5px;
  text-align: center;

  margin: 32px -32px 0 -32px;
  padding: 12px;
  font-size: ${({ styleScheme }) => styleScheme?.footer?.fontSize || '16px'};
  line-height: ${({ styleScheme }) =>
    styleScheme?.footer?.lineHeight || '24px'};
  background-color: ${({ theme, styleScheme }) =>
    styleScheme?.footer?.bg || theme.gray1Color};
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  text-transform: none;

  ${ScButtonWrapper} {
    gap: ${({ styleScheme }) => styleScheme?.footer?.gap || '7px'};
    ${({ styleScheme }) =>
      styleScheme?.footer?.fontColor &&
      css`
        i {
          color: ${styleScheme.footer.fontColor};
        }
      `}
  }

  ${ScButton} {
    ${({ styleScheme }) =>
      styleScheme?.footer?.fontColor &&
      css`
        .text {
          color: ${styleScheme.footer.fontColor};
        }
      `}
    &:hover {
      .text {
        color: rgb(66, 199, 243);
      }
    }
  }

  button {
    min-width: auto;
    padding: 0 2px 0 0;
    overflow: visible;

    .text {
      display: flex;
      font-weight: 700;
      font-size: ${({ styleScheme }) =>
        styleScheme?.footer?.fontSize || '16px'};
      line-height: ${({ styleScheme }) =>
        styleScheme?.footer?.lineHeight || '24px'};
      gap: ${({ styleScheme }) => styleScheme?.footer?.gap || '7px'};
      text-transform: none;
    }

    .awareWrap {
      display: none;
    }

    ${({ styleScheme }) =>
      styleScheme?.footer?.fontColor &&
      css`
        @media (hover: hover) {
          &:hover .text {
            color: ${hexToRGB(styleScheme.footer.fontColor, 0.8)};
          }
        }
      `}
  }

  @media (${device.mobile}) {
    margin: 32px -16px 0 -16px;

    ${({ theme, styleScheme }) =>
      styleScheme?.footer?.mobileBg &&
      css`
        background-color: ${styleScheme.footer.mobileBg || theme.gray1Color};
      `}
  }
`;

export const ScErrorLink = styled.span`
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
`;
